// src/components/Pages/ConfiguracionPanel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import './../../style/Vars.css';
import './../../style/MobileStyles.css';

// 🔹 Endpoints y modelos de Seguridad
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { Models } from "../Seguridad/Utils/ModelsSeguridad";

// 🔹 Usuario en sesión
import { getSessionUserId } from "../../utils/SessionUser";

// Íconos para cada opción de configuración
const submoduleIcons = {
    colores: '🎨',
    logos: '🖼️',
    imagenes: '🌄',
    seguridad: '🔒',
    usuarios: '👥',
    designer: '✨',
    importacion: '📥',
};

// Función auxiliar para asignar colores
const getModuleColor = (id) => {
    const colors = {
        colores: 'rose',
        logos: 'blue',
        imagenes: 'amber',
        seguridad: 'purple',
        usuarios: 'indigo',
        designer: 'cyan',
        importacion: 'emerald',
    };
    return colors[id] || 'cyan';
};

// ===================================================================
// Definición base de los submódulos de Configuración
// ===================================================================
const CONFIG_SUBMODULES = [
    {
        id: "colores",
        title: "Gestión de Colores",
        description: "Personaliza los tonos y temas visuales de la plataforma.",
        path: "/menu/sistema/colores",
    },
    {
        id: "logos",
        title: "Gestión de Logos",
        description: "Administra los logotipos, isotipos y favicon de la empresa.",
        path: "/menu/sistema/logos",
    },
    {
        id: "imagenes",
        title: "Imágenes del Sistema",
        description: "Gestión de fondos, banners e imágenes maestras del ERP.",
        path: "/menu/sistema/imagenes",
    },
    {
        id: "designer",
        title: "Diseñador Visual",
        description: "Diseña y previsualiza temas de forma visual.",
        path: "/menu/sistema/designer",
    },
    {
        id: "importacion",
        title: "Importación Masiva",
        description: "Carga masiva de empleados, cursos y datos.",
        path: "/menu/sistema/importacion",
    },
];

const endpointRecursos = ENDPOINTS.recursos;
const modeloRecurso = Models.catRecursos;

function ConfiguracionPanel() {
    const navigate = useNavigate();
    const [sessionUserId] = useState(() => getSessionUserId());
    const [visibleSubmodules, setVisibleSubmodules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    const handleClick = (path) => {
        // Si el path es /menu/seguridad y no está en App.jsx como subruta, 
        // podrías necesitar manejarlo diferente, pero por ahora seguimos el estándar.
        navigate(path);
    };

    useEffect(() => {
        async function loadRecursos() {
            try {
                if (!sessionUserId) {
                    setErrorMsg("No se pudo identificar al usuario en sesión.");
                    setVisibleSubmodules([]);
                    setLoading(false);
                    return;
                }

                const url = `${endpointRecursos}/${sessionUserId}/Panel`;
                const res = await axios.get(url);
                const recursos = (res.data || []).map(modeloRecurso);

                const allowedIds = new Set(
                    recursos
                        .filter(r => (r.tipo || "").toLowerCase() === "panel" && (r.ruta || "").trim() !== "")
                        .map(r => (r.ruta || "").trim())
                );
                console.log("[ConfigPanel] Recursos permitidos:", [...allowedIds]);

                // Mostramos los módulos según permisos, o todos si es una configuración inicial
                let filtered = CONFIG_SUBMODULES.filter(m => allowedIds.has(m.id));

                setVisibleSubmodules(filtered);
                setLoading(false);

                if (!filtered.length) {
                    setErrorMsg("No tienes opciones de configuración asignadas en este momento.");
                }
            } catch (err) {
                console.error("[ConfigPanel] Error loading resources:", err);
                setErrorMsg("Ocurrió un error al cargar tus permisos de configuración.");
                setVisibleSubmodules([]);
                setLoading(false);
            }
        }

        loadRecursos();
    }, [sessionUserId]);

    return (
        <div className="modules-container">
            <h1>Configuración General del Sistema</h1>

            {loading ? (
                <div className="modules-loading">Cargando opciones...</div>
            ) : errorMsg ? (
                <div className="modules-error">{errorMsg}</div>
            ) : (
                <div className="modules-grid">
                    {visibleSubmodules.map((submodule) => (
                        <div
                            key={submodule.id}
                            className={`module-card module-card--${getModuleColor(submodule.id)}`}
                            onClick={() => handleClick(submodule.path)}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => e.key === 'Enter' && handleClick(submodule.path)}
                        >
                            <div className="module-card__title">{submodule.title}</div>
                            <div className="module-card__description">{submodule.description}</div>
                            <div className="module-card__icon" aria-hidden="true">
                                {submoduleIcons[submodule.id] || '⚙️'}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && !visibleSubmodules.length && !errorMsg && (
                <div className="modules-empty">No tienes permisos para acceder a las opciones de configuración.</div>
            )}
        </div>
    );
}

export default ConfiguracionPanel;
